import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export interface UserProfile {
  id: string;
  name: string | null;
  birth_date: string | null;
  weight_kg: number | null;
  height_cm: number | null;
  gender: string | null;
  weekly_run_frequency: number | null;
  comfortable_pace_seconds: number | null;
  comfortable_pace_unknown: boolean;
  max_heart_rate: number | null;
  resting_heart_rate: number | null;
  rest_days: string[] | null;
  strength_training_days: string[] | null;
  runner_experience: string | null;
  distance_unit: string | null;
  pace_unit: string | null;
  training_intensity: string | null;
  notification_workouts: boolean | null;
  notification_feedback: boolean | null;
  notification_achievements: boolean | null;
  notification_plan_changes: boolean | null;
  language: string | null;
  // RUN-29: timestamp of when the user completed/skipped the product tour.
  // null = never seen (the auto-start gate fires only while this is null).
  onboarding_tour_completed_at: string | null;
}

export function useUserProfile() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['user-profile', user?.id],
    queryFn: async (): Promise<UserProfile | null> => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) {
        // PGRST116 = "no rows returned" from .single() — expected for new users
        if (error.code !== 'PGRST116') {
          console.error('Error fetching user profile:', error);
        }
        return null;
      }

      return data;
    },
    enabled: !!user && !authLoading,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['user-profile'] });
  };

  const updateLanguage = async (language: string) => {
    if (!user) return;
    
    const { error } = await supabase
      .from('user_profiles')
      .update({ language })
      .eq('id', user.id);
    
    if (error) {
      console.error('Error updating language:', error);
      return;
    }
    
    invalidate();
  };

  // RUN-29: stamp the product tour as seen. Called when the tour is completed
  // OR skipped — the auto-start gate keys off this being non-null. Idempotent:
  // re-stamping on a replay just refreshes the timestamp, which is harmless.
  //
  // Memoized so consumers (the tour controller, which depends on it) get a
  // stable reference — an unstable identity here churns the auto-start gate's
  // effect and can cancel its start timer before it fires.
  const completeTour = useCallback(async () => {
    if (!user) return;

    const { error } = await supabase
      .from('user_profiles')
      .update({ onboarding_tour_completed_at: new Date().toISOString() })
      .eq('id', user.id);

    if (error) {
      console.error('Error marking product tour complete:', error);
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['user-profile'] });
  }, [user, queryClient]);

  return {
    profile: query.data,
    isLoading: authLoading || query.isLoading,
    error: query.error,
    refetch: query.refetch,
    invalidate,
    updateLanguage,
    completeTour,
  };
}
